import { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Tab,
  Tabs,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  useTheme,
  alpha,
} from "@mui/material";
import {
  TrendingUp,
  Inventory,
  Business,
  Download,
  DateRange,
  Receipt,
  LocalShipping,
  SwapHoriz,
  Warning,
  Undo,
} from "@mui/icons-material";
import { reportsAPI } from "../../services/API";
import { useSnackbar } from "notistack";

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`tabpanel-${index}`}
    aria-labelledby={`tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const StatCard = ({ title, value, color = "primary", icon, prefix = "" }) => {
  const theme = useTheme();
  
  return (
    <Card 
      sx={{ 
        height: '100%',
        background: `linear-gradient(135deg, ${alpha(theme.palette[color].main, 0.1)} 0%, ${alpha(theme.palette[color].main, 0.05)} 100%)`,
        border: `1px solid ${alpha(theme.palette[color].main, 0.2)}`,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 24px ${alpha(theme.palette[color].main, 0.15)}`,
        }
      }}
    >
      <CardContent sx={{ textAlign: "center", p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mb: 2,
          }}
        >
          {icon && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: "50%",
                backgroundColor: alpha(theme.palette[color].main, 0.1),
                color: theme.palette[color].main,
                mr: 2,
              }}
            >
              {icon}
            </Box>
          )}
          <Box sx={{ textAlign: "left" }}>
            <Typography
              variant="h3"
              fontWeight="bold"
              color={`${color}.main`}
              sx={{ fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' } }}
            >
              {prefix}{value}
            </Typography>
            <Typography 
              variant="h6" 
              color="text.secondary"
              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
            >
              {title}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const SectionHeader = ({ title, subtitle, icon }) => (
  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
    {icon && (
      <Box
        sx={{
          p: 1,
          borderRadius: "12px",
          backgroundColor: "primary.main",
          color: "white",
          mr: 2,
        }}
      >
        {icon}
      </Box>
    )}
    <Box>
      <Typography 
        variant="h4" 
        fontWeight="bold" 
        sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="h6" color="text.secondary" sx={{ mt: 0.5 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  </Box>
);

export const InvReports = () => {
  const [tabValue, setTabValue] = useState(0);
  const [departmentConsumptionReport, setDepartmentConsumptionReport] =
    useState(null);
  const [inventoryReport, setInventoryReport] = useState(null);
  const [itemUsageReport, setItemUsageReport] = useState(null);
  const [purchaseOrdersReport, setPurchaseOrdersReport] = useState(null);
  const [supplierPerformanceReport, setSupplierPerformanceReport] =
    useState(null);
  const [stockMovementReport, setStockMovementReport] = useState(null);
  const [expiryReport, setExpiryReport] = useState(null);
  const [returnsReport, setReturnsReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: (() => {
      const date = new Date();
      date.setDate(date.getDate() - 30);
      return date.toISOString().split("T")[0];
    })(),
    endDate: new Date().toISOString().split("T")[0],
  });

  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  useEffect(() => {
    fetchReports();
  }, [tabValue, dateRange]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      switch (tabValue) {
        case 0:
          const deptConsumptionResponse =
            await reportsAPI.getDepartmentConsumptionReport({
              startDate: dateRange.startDate,
              endDate: dateRange.endDate,
            });
          setDepartmentConsumptionReport(deptConsumptionResponse.data.data);
          break;
        case 1:
          const inventoryResponse = await reportsAPI.getInventoryReport({
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
          });
          setInventoryReport(inventoryResponse.data.data);
          break;
        case 2:
          const itemUsageResponse = await reportsAPI.getItemUsageReport({
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
          });
          setItemUsageReport(itemUsageResponse.data.data);
          break;
        case 3:
          const poResponse = await reportsAPI.getPurchaseOrdersReport({
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
          });
          setPurchaseOrdersReport(poResponse.data.data);
          break;
        case 4:
          const supplierResponse =
            await reportsAPI.getSupplierPerformanceReport({
              startDate: dateRange.startDate,
              endDate: dateRange.endDate,
            });
          setSupplierPerformanceReport(supplierResponse.data.data);
          break;
        case 5:
          const stockMovementResponse = await reportsAPI.getStockMovementReport(
            {
              startDate: dateRange.startDate,
              endDate: dateRange.endDate,
            }
          );
          setStockMovementReport(stockMovementResponse.data.data);
          break;
        case 6:
          const expiryResponse = await reportsAPI.getExpiryReport();
          setExpiryReport(expiryResponse.data.data);
          break;
        case 7:
          const returnsResponse = await reportsAPI.getReturnsReport({
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
          });
          setReturnsReport(returnsResponse.data.data);
          break;
        default:
          break;
      }
    } catch (error) {
      enqueueSnackbar("Error fetching report data", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_event, newValue) => {
    setTabValue(newValue);
  };

  const handleDateChange = (field, value) => {
    setDateRange((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleExport = async () => {
    try {
      const reportTypes = [
        "department-consumption",
        "inventory",
        "item-usage",
        "purchase-orders",
        "supplier-performance",
        "stock-movement",
        "expiry-alerts",
        "returns",
      ];

      const reportType = reportTypes[tabValue];
      const response = await reportsAPI.exportReport(reportType, dateRange);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${reportType}-report-${new Date().toISOString().split("T")[0]}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();

      enqueueSnackbar("Report exported successfully!", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Error exporting report", { variant: "error" });
    }
  };

  return (
    <Box  
      sx={{
        minHeight:'100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          mb: 4,
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
        }}
      >
        <Box>
          <Typography 
            variant="h2" 
            fontWeight="bold" 
            sx={{ 
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
            }}
          >
            Reports & Analytics
          </Typography>
          <Typography 
            variant="h6" 
            color="text.secondary" 
            sx={{ mt: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}
          >
            Comprehensive inventory insights and analytics
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          startIcon={<Download />}
          onClick={handleExport}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 3,
            fontSize: '1.1rem',
            fontWeight: 'bold',
          }}
        >
          Export Report
        </Button>
      </Box>

      {/* Date Range Selector */}
      <Card 
        sx={{ 
          mb: 4,
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
          backdropFilter: 'blur(10px)',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item>
              <DateRange sx={{ fontSize: '2rem', color: 'primary.main' }} />
            </Grid>
            <Grid item xs={12} sm={5} md={3}>
              <TextField
                type="date"
                label="Start Date"
                value={dateRange.startDate}
                onChange={(e) => handleDateChange("startDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="medium"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontSize: '1.1rem',
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={5} md={3}>
              <TextField
                type="date"
                label="End Date"
                value={dateRange.endDate}
                onChange={(e) => handleDateChange("endDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="medium"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontSize: '1.1rem',
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={2} md={2}>
              <Button 
                variant="contained" 
                onClick={fetchReports}
                fullWidth
                size="large"
                sx={{ 
                  py: 1.5,
                  borderRadius: 2,
                  fontSize: '1.1rem',
                }}
              >
                Apply
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs Section */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              px: 3,
              pt: 2,
              '& .MuiTab-root': {
                fontSize: '1rem',
                fontWeight: 'bold',
                minHeight: 60,
                minWidth: { xs: 120, sm: 160 },
                '&.Mui-selected': {
                  color: 'primary.main',
                },
              },
            }}
          >
            <Tab icon={<Business />} label="Department Consumption" />
            <Tab icon={<Inventory />} label="Inventory Report" />
            <Tab icon={<TrendingUp />} label="Item Usage" />
            <Tab icon={<Receipt />} label="Purchase Orders" />
            <Tab icon={<LocalShipping />} label="Supplier Performance" />
            <Tab icon={<SwapHoriz />} label="Stock Movement" />
            <Tab icon={<Warning />} label="Expiry Alerts" />
            <Tab icon={<Undo />} label="Returns Report" />
          </Tabs>

          {loading && <LinearProgress sx={{ height: 3 }} />}

          {/* Department Consumption Report Tab */}
          <TabPanel value={tabValue} index={0}>
            {departmentConsumptionReport && (
              <Box>
                <SectionHeader
                  title="Department Consumption Summary"
                  subtitle={`${new Date(
                    departmentConsumptionReport.period.start
                  ).toLocaleDateString()} - ${new Date(
                    departmentConsumptionReport.period.end
                  ).toLocaleDateString()}`}
                  icon={<Business />}
                />

                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                      title="Total Item Issues"
                      value={departmentConsumptionReport.summary.totalIssues}
                      color="primary"
                      icon={<Business />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                      title="Total Items Issued"
                      value={departmentConsumptionReport.summary.totalItemsIssued}
                      color="success"
                      icon={<Inventory />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                      title="Total Cost"
                      value={departmentConsumptionReport.summary.totalCost.toLocaleString()}
                      color="info"
                      icon={<TrendingUp />}
                      prefix="₹"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                      title="Active Departments"
                      value={departmentConsumptionReport.summary.activeDepartments}
                      color="warning"
                      icon={<Business />}
                    />
                  </Grid>
                </Grid>

                {/* Top Consuming Departments */}
                <Card sx={{ mb: 4 }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography 
                      variant="h5" 
                      gutterBottom 
                      fontWeight="bold"
                      sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }}
                    >
                      Top Consuming Departments
                    </Typography>
                    <TableContainer 
                      component={Paper} 
                      variant="outlined"
                      sx={{ borderRadius: 3, overflow: 'hidden' }}
                    >
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Department</TableCell>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Type</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Issues</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Items Issued</TableCell>
                            <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Total Cost</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {departmentConsumptionReport?.topConsumingDepartments?.map(
                            (dept, index) => (
                              <TableRow key={index} hover>
                                <TableCell sx={{ py: 2 }}>
                                  <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                    {dept.department.departmentName}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {dept.department.departmentCode}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ py: 2 }}>
                                  <Chip
                                    label={dept.department.departmentType}
                                    size="medium"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ fontSize: '0.9rem' }}
                                  />
                                </TableCell>
                                <TableCell align="center" sx={{ py: 2 }}>
                                  <Chip 
                                    label={dept.issueCount} 
                                    size="medium" 
                                    sx={{ fontSize: '1rem' }}
                                  />
                                </TableCell>
                                <TableCell align="center" sx={{ py: 2 }}>
                                  <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                    {dept.totalQuantity}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ py: 2 }}>
                                  <Typography
                                    variant="h6"
                                    color="success.main"
                                    sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}
                                  >
                                    ₹{dept.totalCost.toLocaleString()}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>

                {/* Consumption by Purpose */}
                <Card>
                  <CardContent sx={{ p: 4 }}>
                    <Typography 
                      variant="h5" 
                      gutterBottom 
                      fontWeight="bold"
                      sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }}
                    >
                      Consumption by Purpose
                    </Typography>
                    <TableContainer 
                      component={Paper} 
                      variant="outlined"
                      sx={{ borderRadius: 3, overflow: 'hidden' }}
                    >
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: alpha(theme.palette.secondary.main, 0.05) }}>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Purpose</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Issues</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Items</TableCell>
                            <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Cost</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {departmentConsumptionReport?.consumptionByPurpose?.map(
                            (purpose, index) => (
                              <TableRow key={index} hover>
                                <TableCell sx={{ py: 2 }}>
                                  <Chip
                                    label={purpose.purpose}
                                    size="medium"
                                    color="secondary"
                                    sx={{ fontSize: '1rem' }}
                                  />
                                </TableCell>
                                <TableCell align="center" sx={{ py: 2, fontSize: '1.1rem' }}>
                                  {purpose.count}
                                </TableCell>
                                <TableCell align="center" sx={{ py: 2, fontSize: '1.1rem' }}>
                                  {purpose.totalQuantity}
                                </TableCell>
                                <TableCell align="right" sx={{ py: 2 }}>
                                  <Typography
                                    variant="h6"
                                    color="success.main"
                                    sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}
                                  >
                                    ₹{purpose.totalCost.toLocaleString()}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Box>
            )}
          </TabPanel>

          {/* Inventory Report Tab */}
          <TabPanel value={tabValue} index={1}>
            {inventoryReport && (
              <Box>
                <SectionHeader
                  title="Inventory Overview"
                  subtitle="Current inventory status and valuation"
                  icon={<Inventory />}
                />

                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                      title="Total Items"
                      value={inventoryReport.summary.totalItems}
                      color="primary"
                      icon={<Inventory />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                      title="Low Stock Items"
                      value={inventoryReport.summary.lowStockCount}
                      color="warning"
                      icon={<Warning />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                      title="Expiring Soon"
                      value={inventoryReport.summary.expiringCount}
                      color="error"
                      icon={<Warning />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                      title="Total Value"
                      value={inventoryReport.summary.totalValue.toLocaleString()}
                      color="success"
                      icon={<TrendingUp />}
                      prefix="₹"
                    />
                  </Grid>
                </Grid>

                {/* Inventory by Category */}
                <Card sx={{ mb: 4 }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography 
                      variant="h5" 
                      gutterBottom 
                      fontWeight="bold"
                      sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }}
                    >
                      Inventory by Category
                    </Typography>
                    <TableContainer 
                      component={Paper} 
                      variant="outlined"
                      sx={{ borderRadius: 3, overflow: 'hidden' }}
                    >
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Category</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Items</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Total Quantity</TableCell>
                            <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Total Value</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {inventoryReport.inventoryByCategory.map(
                            (category, index) => (
                              <TableRow key={index} hover>
                                <TableCell sx={{ py: 2 }}>
                                  <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                    {category.category}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center" sx={{ py: 2 }}>
                                  <Chip label={category.items} size="medium" sx={{ fontSize: '1rem' }} />
                                </TableCell>
                                <TableCell align="center" sx={{ py: 2 }}>
                                  <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                    {category.totalQuantity}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ py: 2 }}>
                                  <Typography
                                    variant="h6"
                                    color="success.main"
                                    sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}
                                  >
                                    ₹{category.totalValue.toLocaleString()}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>

                {/* Inventory by Item Type */}
                <Card>
                  <CardContent sx={{ p: 4 }}>
                    <Typography 
                      variant="h5" 
                      gutterBottom 
                      fontWeight="bold"
                      sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }}
                    >
                      Inventory by Item Type
                    </Typography>
                    <TableContainer 
                      component={Paper} 
                      variant="outlined"
                      sx={{ borderRadius: 3, overflow: 'hidden' }}
                    >
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: alpha(theme.palette.secondary.main, 0.05) }}>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Item Type</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Items</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Total Quantity</TableCell>
                            <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Total Value</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {inventoryReport.inventoryByItemType?.map(
                            (itemType, index) => (
                              <TableRow key={index} hover>
                                <TableCell sx={{ py: 2 }}>
                                  <Chip
                                    label={itemType.itemType}
                                    size="medium"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ fontSize: '1rem' }}
                                  />
                                </TableCell>
                                <TableCell align="center" sx={{ py: 2 }}>
                                  <Chip label={itemType.items} size="medium" sx={{ fontSize: '1rem' }} />
                                </TableCell>
                                <TableCell align="center" sx={{ py: 2 }}>
                                  <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                    {itemType.totalQuantity}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ py: 2 }}>
                                  <Typography
                                    variant="h6"
                                    color="success.main"
                                    sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}
                                  >
                                    ₹{itemType.totalValue.toLocaleString()}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Box>
            )}
          </TabPanel>

          {/* Item Usage Report Tab */}
          <TabPanel value={tabValue} index={2}>
            {itemUsageReport && (
              <Box>
                <SectionHeader
                  title="Item Usage Report"
                  subtitle={`${new Date(itemUsageReport.period.start).toLocaleDateString()} - ${new Date(itemUsageReport.period.end).toLocaleDateString()}`}
                  icon={<TrendingUp />}
                />

                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                      title="Items Used"
                      value={itemUsageReport.summary.totalItems}
                      color="primary"
                      icon={<Inventory />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                      title="Total Quantity"
                      value={itemUsageReport.summary.totalQuantity}
                      color="success"
                      icon={<TrendingUp />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                      title="Total Cost"
                      value={itemUsageReport.summary.totalCost.toLocaleString()}
                      color="info"
                      icon={<Receipt />}
                      prefix="₹"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                      title="Departments Served"
                      value={itemUsageReport.summary.departmentsServed}
                      color="warning"
                      icon={<Business />}
                    />
                  </Grid>
                </Grid>

                {/* Most Used Items */}
                <Card sx={{ mb: 4 }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography 
                      variant="h5" 
                      gutterBottom 
                      fontWeight="bold"
                      sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }}
                    >
                      Most Used Items
                    </Typography>
                    <TableContainer 
                      component={Paper} 
                      variant="outlined"
                      sx={{ borderRadius: 3, overflow: 'hidden' }}
                    >
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Item</TableCell>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Type</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Quantity Issued</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Issues</TableCell>
                            <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Total Cost</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {itemUsageReport?.mostUsedItems?.map(
                            (item, index) => (
                              <TableRow key={index} hover>
                                <TableCell sx={{ py: 2 }}>
                                  <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                    {item.item.name}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {item.item.brand}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ py: 2 }}>
                                  <Chip
                                    label={item.item.itemType}
                                    size="medium"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ fontSize: '0.9rem' }}
                                  />
                                </TableCell>
                                <TableCell align="center" sx={{ py: 2 }}>
                                  <Chip label={item.quantity} size="medium" sx={{ fontSize: '1rem' }} />
                                </TableCell>
                                <TableCell align="center" sx={{ py: 2 }}>
                                  <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                    {item.issueCount}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ py: 2 }}>
                                  <Typography
                                    variant="h6"
                                    color="success.main"
                                    sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}
                                  >
                                    ₹{item.totalCost.toLocaleString()}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>

                {/* Usage Trends by Department Type */}
                <Card>
                  <CardContent sx={{ p: 4 }}>
                    <Typography 
                      variant="h5" 
                      gutterBottom 
                      fontWeight="bold"
                      sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }}
                    >
                      Usage by Department Type
                    </Typography>
                    <TableContainer 
                      component={Paper} 
                      variant="outlined"
                      sx={{ borderRadius: 3, overflow: 'hidden' }}
                    >
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: alpha(theme.palette.secondary.main, 0.05) }}>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Department Type</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Departments</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Items Issued</TableCell>
                            <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Total Cost</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {itemUsageReport?.usageByDepartmentType?.map(
                            (deptType, index) => (
                              <TableRow key={index} hover>
                                <TableCell sx={{ py: 2 }}>
                                  <Chip
                                    label={deptType.departmentType}
                                    size="medium"
                                    color="secondary"
                                    sx={{ fontSize: '1rem' }}
                                  />
                                </TableCell>
                                <TableCell align="center" sx={{ py: 2, fontSize: '1.1rem' }}>
                                  {deptType.departmentCount}
                                </TableCell>
                                <TableCell align="center" sx={{ py: 2 }}>
                                  <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                    {deptType.totalQuantity}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ py: 2 }}>
                                  <Typography
                                    variant="h6"
                                    color="success.main"
                                    sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}
                                  >
                                    ₹{deptType.totalCost.toLocaleString()}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Box>
            )}
          </TabPanel>

          {/* Purchase Orders Report Tab */}
          <TabPanel value={tabValue} index={3}>
            {purchaseOrdersReport ? (
              <Box>
                <SectionHeader
                  title="Purchase Orders Report"
                  subtitle="Purchase order tracking and analysis"
                  icon={<Receipt />}
                />

                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                      title="Total Orders"
                      value={purchaseOrdersReport.summary.totalOrders}
                      color="primary"
                      icon={<Receipt />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                      title="Total Amount"
                      value={purchaseOrdersReport.summary.totalAmount.toLocaleString()}
                      color="success"
                      icon={<TrendingUp />}
                      prefix="₹"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                      title="Pending"
                      value={purchaseOrdersReport.summary.pendingOrders}
                      color="warning"
                      icon={<Warning />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                      title="Completed"
                      value={purchaseOrdersReport.summary.completedOrders}
                      color="info"
                      icon={<Inventory />}
                    />
                  </Grid>
                </Grid>

                {/* Recent Purchase Orders */}
                <Card sx={{ mb: 4 }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography 
                      variant="h5" 
                      gutterBottom 
                      fontWeight="bold"
                      sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }}
                    >
                      Recent Purchase Orders
                    </Typography>
                    <TableContainer 
                      component={Paper} 
                      variant="outlined"
                      sx={{ borderRadius: 3, overflow: 'hidden' }}
                    >
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>PO Number</TableCell>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Supplier</TableCell>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Date</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Status</TableCell>
                            <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Amount</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {purchaseOrdersReport.recentOrders?.slice(0, 10).map((order) => (
                            <TableRow key={order._id} hover>
                              <TableCell sx={{ py: 2 }}>
                                <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                  {order.poNumber}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 2 }}>
                                <Typography variant="body2" sx={{ fontSize: '1.1rem' }}>
                                  {order.supplier?.name || 'N/A'}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 2, fontSize: '1.1rem' }}>
                                {new Date(order.orderDate).toLocaleDateString()}
                              </TableCell>
                              <TableCell align="center" sx={{ py: 2 }}>
                                <Chip
                                  label={order.status}
                                  size="medium"
                                  color={
                                    order.status === 'completed' ? 'success' :
                                      order.status === 'confirmed' ? 'info' :
                                        order.status === 'sent' ? 'warning' :
                                          'default'
                                  }
                                  sx={{ fontSize: '0.9rem' }}
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                <Typography variant="h6" color="success.main" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                                  ₹{order.grandTotal.toLocaleString()}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>

                {/* Top Suppliers */}
                <Card>
                  <CardContent sx={{ p: 4 }}>
                    <Typography 
                      variant="h5" 
                      gutterBottom 
                      fontWeight="bold"
                      sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }}
                    >
                      Top Suppliers by Purchase Value
                    </Typography>
                    <TableContainer 
                      component={Paper} 
                      variant="outlined"
                      sx={{ borderRadius: 3, overflow: 'hidden' }}
                    >
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: alpha(theme.palette.secondary.main, 0.05) }}>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Supplier</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Orders</TableCell>
                            <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Total Amount</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {purchaseOrdersReport.topSuppliers?.map((supplier, index) => (
                            <TableRow key={index} hover>
                              <TableCell sx={{ py: 2 }}>
                                <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                  {supplier.supplier?.name || 'Unknown'}
                                </Typography>
                              </TableCell>
                              <TableCell align="center" sx={{ py: 2 }}>
                                <Chip label={supplier.orderCount} size="medium" sx={{ fontSize: '1rem' }} />
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                <Typography variant="h6" color="success.main" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                                  ₹{supplier.totalAmount.toLocaleString()}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Box>
            ) : (
              <Typography variant="h6" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                No data available
              </Typography>
            )}
          </TabPanel>

          {/* Supplier Performance Report Tab */}
          <TabPanel value={tabValue} index={4}>
            {supplierPerformanceReport ? (
              <Box>
                <SectionHeader
                  title="Supplier Performance Report"
                  subtitle="Supplier performance metrics and analysis"
                  icon={<LocalShipping />}
                />

                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={6} lg={4}>
                    <StatCard
                      title="Total Suppliers"
                      value={supplierPerformanceReport.summary.totalSuppliers}
                      color="primary"
                      icon={<LocalShipping />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={4}>
                    <StatCard
                      title="Total Orders"
                      value={supplierPerformanceReport.summary.totalOrders}
                      color="success"
                      icon={<Receipt />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={4}>
                    <StatCard
                      title="Total Amount"
                      value={supplierPerformanceReport.summary.totalAmount.toLocaleString()}
                      color="info"
                      icon={<TrendingUp />}
                      prefix="₹"
                    />
                  </Grid>
                </Grid>

                {/* Supplier Performance Table */}
                <Card>
                  <CardContent sx={{ p: 4 }}>
                    <Typography 
                      variant="h5" 
                      gutterBottom 
                      fontWeight="bold"
                      sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }}
                    >
                      Supplier Performance Details
                    </Typography>
                    <TableContainer 
                      component={Paper} 
                      variant="outlined"
                      sx={{ borderRadius: 3, overflow: 'hidden' }}
                    >
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Supplier</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Total Orders</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Completed</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Pending</TableCell>
                            <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Total Amount</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {supplierPerformanceReport.supplierPerformance?.map((supplier, index) => (
                            <TableRow key={index} hover>
                              <TableCell sx={{ py: 2 }}>
                                <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                  {supplier.supplier?.name || 'Unknown'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {supplier.supplier?.contactPerson || ''}
                                </Typography>
                              </TableCell>
                              <TableCell align="center" sx={{ py: 2 }}>
                                <Chip label={supplier.totalOrders} size="medium" color="primary" sx={{ fontSize: '1rem' }} />
                              </TableCell>
                              <TableCell align="center" sx={{ py: 2 }}>
                                <Chip label={supplier.completedOrders} size="medium" color="success" sx={{ fontSize: '1rem' }} />
                              </TableCell>
                              <TableCell align="center" sx={{ py: 2 }}>
                                <Chip label={supplier.pendingOrders} size="medium" color="warning" sx={{ fontSize: '1rem' }} />
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                <Typography variant="h6" color="success.main" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                                  ₹{supplier.totalAmount.toLocaleString()}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Box>
            ) : (
              <Typography variant="h6" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                No data available
              </Typography>
            )}
          </TabPanel>

          {/* Stock Movement Report Tab */}
          <TabPanel value={tabValue} index={5}>
            {stockMovementReport ? (
              <Box>
                <SectionHeader
                  title="Stock Movement Report"
                  subtitle="Stock in and out movement tracking"
                  icon={<SwapHoriz />}
                />

                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                      title="Stock In"
                      value={stockMovementReport.summary.totalStockIn}
                      color="success"
                      icon={<TrendingUp />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                      title="Stock Out"
                      value={stockMovementReport.summary.totalStockOut}
                      color="error"
                      icon={<TrendingUp />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                      title="In Value"
                      value={stockMovementReport.summary.stockInValue.toLocaleString()}
                      color="primary"
                      icon={<Receipt />}
                      prefix="₹"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                      title="Out Value"
                      value={stockMovementReport.summary.stockOutValue.toLocaleString()}
                      color="warning"
                      icon={<Receipt />}
                      prefix="₹"
                    />
                  </Grid>
                </Grid>

                {/* Stock In Table */}
                <Card sx={{ mb: 4 }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography 
                      variant="h5" 
                      gutterBottom 
                      fontWeight="bold"
                      sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }}
                    >
                      Stock In (Purchases)
                    </Typography>
                    <TableContainer 
                      component={Paper} 
                      variant="outlined"
                      sx={{ borderRadius: 3, overflow: 'hidden' }}
                    >
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: alpha(theme.palette.success.main, 0.05) }}>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>PO Number</TableCell>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Supplier</TableCell>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Date</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Items</TableCell>
                            <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Amount</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {stockMovementReport.stockIn?.map((po) => (
                            <TableRow key={po._id} hover>
                              <TableCell sx={{ py: 2 }}>
                                <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                  {po.poNumber}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 2, fontSize: '1.1rem' }}>
                                {po.supplier?.name || 'N/A'}
                              </TableCell>
                              <TableCell sx={{ py: 2, fontSize: '1.1rem' }}>
                                {new Date(po.orderDate).toLocaleDateString()}
                              </TableCell>
                              <TableCell align="center" sx={{ py: 2 }}>
                                <Chip
                                  label={po.items.reduce((sum, item) => sum + item.quantity, 0)}
                                  size="medium"
                                  color="success"
                                  sx={{ fontSize: '1rem' }}
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                <Typography variant="h6" color="success.main" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                                  ₹{po.grandTotal.toLocaleString()}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>

                {/* Stock Out Table */}
                <Card>
                  <CardContent sx={{ p: 4 }}>
                    <Typography 
                      variant="h5" 
                      gutterBottom 
                      fontWeight="bold"
                      sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }}
                    >
                      Stock Out (Issues)
                    </Typography>
                    <TableContainer 
                      component={Paper} 
                      variant="outlined"
                      sx={{ borderRadius: 3, overflow: 'hidden' }}
                    >
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: alpha(theme.palette.error.main, 0.05) }}>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Issue Number</TableCell>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Department</TableCell>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Date</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Items</TableCell>
                            <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Cost</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {stockMovementReport.stockOut?.map((issue) => (
                            <TableRow key={issue._id} hover>
                              <TableCell sx={{ py: 2 }}>
                                <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                  {issue.issueNumber}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 2, fontSize: '1.1rem' }}>
                                {issue.department?.departmentName || 'N/A'}
                              </TableCell>
                              <TableCell sx={{ py: 2, fontSize: '1.1rem' }}>
                                {new Date(issue.issueDate).toLocaleDateString()}
                              </TableCell>
                              <TableCell align="center" sx={{ py: 2 }}>
                                <Chip
                                  label={issue.items.reduce((sum, item) => sum + item.quantity, 0)}
                                  size="medium"
                                  color="error"
                                  sx={{ fontSize: '1rem' }}
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                <Typography variant="h6" color="error.main" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                                  ₹{issue.totalCost.toLocaleString()}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Box>
            ) : (
              <Typography variant="h6" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                No data available
              </Typography>
            )}
          </TabPanel>

          {/* Expiry Alerts Report Tab */}
          <TabPanel value={tabValue} index={6}>
            {expiryReport ? (
              <Box>
                <SectionHeader
                  title="Expiry Alerts Report"
                  subtitle="Product expiry tracking and alerts"
                  icon={<Warning />}
                />

                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                      title="Expiring Soon"
                      value={expiryReport.summary.expiringCount}
                      color="warning"
                      icon={<Warning />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                      title="Expired"
                      value={expiryReport.summary.expiredCount}
                      color="error"
                      icon={<Warning />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                      title="Expiring Value"
                      value={expiryReport.summary.expiringValue.toLocaleString()}
                      color="info"
                      icon={<TrendingUp />}
                      prefix="₹"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                      title="Expired Value"
                      value={expiryReport.summary.expiredValue.toLocaleString()}
                      color="error"
                      icon={<TrendingUp />}
                      prefix="₹"
                    />
                  </Grid>
                </Grid>

                {/* Expiring Soon Table */}
                <Card sx={{ mb: 4 }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography 
                      variant="h5" 
                      gutterBottom 
                      fontWeight="bold"
                      sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }}
                    >
                      Items Expiring Soon (Next {expiryReport.alertDays} Days)
                    </Typography>
                    <TableContainer 
                      component={Paper} 
                      variant="outlined"
                      sx={{ borderRadius: 3, overflow: 'hidden' }}
                    >
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: alpha(theme.palette.warning.main, 0.05) }}>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Item</TableCell>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Batch Number</TableCell>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Supplier</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Expiry Date</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Available Qty</TableCell>
                            <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Value</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {expiryReport.expiringBatches?.map((batch) => {
                            const daysToExpiry = Math.ceil(
                              (new Date(batch.expiryDate) - new Date()) / (1000 * 60 * 60 * 24)
                            );
                            return (
                              <TableRow key={batch._id} hover>
                                <TableCell sx={{ py: 2 }}>
                                  <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                    {batch.item?.name || 'N/A'}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {batch.item?.brand}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ py: 2, fontSize: '1.1rem' }}>{batch.batchNumber}</TableCell>
                                <TableCell sx={{ py: 2, fontSize: '1.1rem' }}>{batch.supplier?.name || 'N/A'}</TableCell>
                                <TableCell align="center" sx={{ py: 2 }}>
                                  <Chip
                                    label={new Date(batch.expiryDate).toLocaleDateString()}
                                    size="medium"
                                    color={daysToExpiry <= 7 ? 'error' : 'warning'}
                                    sx={{ fontSize: '0.9rem' }}
                                  />
                                  <Typography variant="body2" display="block" color="warning.main">
                                    {daysToExpiry} days left
                                  </Typography>
                                </TableCell>
                                <TableCell align="center" sx={{ py: 2 }}>
                                  <Chip label={batch.availableQuantity} size="medium" sx={{ fontSize: '1rem' }} />
                                </TableCell>
                                <TableCell align="right" sx={{ py: 2 }}>
                                  <Typography variant="h6" color="warning.main" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                                    ₹{(batch.availableQuantity * batch.costPrice).toLocaleString()}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>

                {/* Expired Items Table */}
                <Card>
                  <CardContent sx={{ p: 4 }}>
                    <Typography 
                      variant="h5" 
                      gutterBottom 
                      fontWeight="bold"
                      sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }}
                    >
                      Expired Items
                    </Typography>
                    <TableContainer 
                      component={Paper} 
                      variant="outlined"
                      sx={{ borderRadius: 3, overflow: 'hidden' }}
                    >
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: alpha(theme.palette.error.main, 0.05) }}>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Item</TableCell>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Batch Number</TableCell>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Supplier</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Expired On</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Available Qty</TableCell>
                            <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Value</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {expiryReport.expiredBatches?.map((batch) => (
                            <TableRow key={batch._id} hover>
                              <TableCell sx={{ py: 2 }}>
                                <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                  {batch.item?.name || 'N/A'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {batch.item?.brand}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 2, fontSize: '1.1rem' }}>{batch.batchNumber}</TableCell>
                              <TableCell sx={{ py: 2, fontSize: '1.1rem' }}>{batch.supplier?.name || 'N/A'}</TableCell>
                              <TableCell align="center" sx={{ py: 2 }}>
                                <Chip
                                  label={new Date(batch.expiryDate).toLocaleDateString()}
                                  size="medium"
                                  color="error"
                                  sx={{ fontSize: '0.9rem' }}
                                />
                              </TableCell>
                              <TableCell align="center" sx={{ py: 2 }}>
                                <Chip label={batch.availableQuantity} size="medium" color="error" sx={{ fontSize: '1rem' }} />
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                <Typography variant="h6" color="error.main" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                                  ₹{(batch.availableQuantity * batch.costPrice).toLocaleString()}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Box>
            ) : (
              <Typography variant="h6" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                No data available
              </Typography>
            )}
          </TabPanel>

          {/* Returns Report Tab */}
          <TabPanel value={tabValue} index={7}>
            {returnsReport ? (
              <Box>
                <SectionHeader
                  title="Returns Report"
                  subtitle="Item returns tracking and analysis"
                  icon={<Undo />}
                />

                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                      title="Total Returns"
                      value={returnsReport.summary.totalReturns}
                      color="primary"
                      icon={<Undo />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                      title="Total Amount"
                      value={returnsReport.summary.totalAmount.toLocaleString()}
                      color="success"
                      icon={<TrendingUp />}
                      prefix="₹"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                      title="Pending"
                      value={returnsReport.summary.pendingReturns}
                      color="warning"
                      icon={<Warning />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                      title="Completed"
                      value={returnsReport.summary.completedReturns}
                      color="info"
                      icon={<Inventory />}
                    />
                  </Grid>
                </Grid>

                {/* Recent Returns Table */}
                <Card>
                  <CardContent sx={{ p: 4 }}>
                    <Typography 
                      variant="h5" 
                      gutterBottom 
                      fontWeight="bold"
                      sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }}
                    >
                      Recent Returns
                    </Typography>
                    <TableContainer 
                      component={Paper} 
                      variant="outlined"
                      sx={{ borderRadius: 3, overflow: 'hidden' }}
                    >
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Return Number</TableCell>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Department</TableCell>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Issue Number</TableCell>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Date</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Items</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Status</TableCell>
                            <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Amount</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {returnsReport.recentReturns?.map((returnItem) => (
                            <TableRow key={returnItem._id} hover>
                              <TableCell sx={{ py: 2 }}>
                                <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                  {returnItem.returnNumber || 'N/A'}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 2, fontSize: '1.1rem' }}>
                                {returnItem.department?.departmentName || 'N/A'}
                              </TableCell>
                              <TableCell sx={{ py: 2, fontSize: '1.1rem' }}>
                                {returnItem.itemIssue?.issueNumber || 'N/A'}
                              </TableCell>
                              <TableCell sx={{ py: 2, fontSize: '1.1rem' }}>
                                {new Date(returnItem.createdAt).toLocaleDateString()}
                              </TableCell>
                              <TableCell align="center" sx={{ py: 2 }}>
                                <Chip
                                  label={returnItem.items?.length || 0}
                                  size="medium"
                                  sx={{ fontSize: '1rem' }}
                                />
                              </TableCell>
                              <TableCell align="center" sx={{ py: 2 }}>
                                <Chip
                                  label={returnItem.status}
                                  size="medium"
                                  color={
                                    returnItem.status === 'completed' ? 'success' :
                                      returnItem.status === 'approved' ? 'info' :
                                        returnItem.status === 'pending' ? 'warning' :
                                          returnItem.status === 'rejected' ? 'error' :
                                            'default'
                                  }
                                  sx={{ fontSize: '0.9rem' }}
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                <Typography variant="h6" color="success.main" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                                  ₹{returnItem.totalAmount?.toLocaleString() || 0}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Box>
            ) : (
              <Typography variant="h6" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                No data available
              </Typography>
            )}
          </TabPanel>
        </CardContent>
      </Card>
    </Box>
    </Box>
  );
};